import * as React from "react";
import { TradesGrid, DefaultSpotFxColumns } from "./TradesGrid";
import { AppState } from "../Store/RootReducer";
import { connect } from "react-redux";
import {
  Trade,
  Consideration,
  Currency,
  TradeSubType
} from "swan-public-api-client";
import { Table } from "antd";
import { ColumnProps, PaginationConfig, TableLocale } from "antd/lib/table";
import { considerationRenderer, dateRenderer } from "../Grid/Renderers";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { getDateSorter, getConsiderationSorter } from "../Grid/Sorters";
import { FxConvertedTrade } from "./FxConvertedTrade";

interface PropsFromState {
  currencies: Map<number, Currency>;
}

export interface TradesByMarketAndValueDateGridProps {
  marketKey: string;
  bordered?: boolean;
  pagination: PaginationConfig | false;
  title?: boolean | string | ((currentPageData: object[]) => React.ReactNode);
  locale?: TableLocale;
}

interface Props
  extends PropsFromState,
    WrappedComponentProps,
    TradesByMarketAndValueDateGridProps {
  trades: FxConvertedTrade[];
}

interface ValueDateSummary {
  valueDate: Date | undefined;
  consideration: Consideration;
}

interface ByValueDateTrades {
  summary: ValueDateSummary;
  trades: FxConvertedTrade[];
}

class TradesByMarketAndValueDateGridCmp extends React.PureComponent<Props> {
  public render() {
    const columns: Array<ColumnProps<ByValueDateTrades>> = [
      {
        dataIndex: "summary.valueDate",
        title: this.props.intl.formatMessage({
          id: "tradesByMarketAndValueDateGrid.valueDate"
        }),
        sorter: getDateSorter(x => x.summary.valueDate),
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "ascend",
        render: dateRenderer(x => x.summary.valueDate)
      },
      {
        dataIndex: "summary.consideration.BaseConsideration.Value",
        title: this.props.intl.formatMessage({
          id: "tradesByMarketAndValueDateGrid.baseCurrencyConsideration"
        }),
        render: considerationRenderer(
          x => x.summary.consideration.BaseConsideration,
          this.props.currencies
        ),
        align: "right",
        sorter: getConsiderationSorter(x => x.summary.consideration.BaseConsideration)
      },
      {
        dataIndex: "summary.consideration.DeliveryConsideration.Value",
        title: this.props.intl.formatMessage({
          id: "tradesByMarketAndValueDateGrid.deliveryCurrencyConsideration"
        }),
        render: considerationRenderer(
          x => x.summary.consideration.DeliveryConsideration,
          this.props.currencies
        ),
        align: "right",
        sorter: getConsiderationSorter(x => x.summary.consideration.DeliveryConsideration)
      }
    ];
    const tradesOnMarketByValueDate = this.getNonConversionTradesOnMarketByValueDate(
      this.props.trades
    );

    return (
      <Table
        dataSource={tradesOnMarketByValueDate}
        rowKey={(record: ByValueDateTrades) =>
          record.summary.valueDate
            ? record.summary.valueDate.getTime().toString()
            : "undefined"
        }
        columns={columns}
        expandedRowRender={this.expandedRowRender}
        size="middle"
        bordered={this.props.bordered}
        pagination={this.props.pagination}
        title={this.getTitle()}
        locale={this.props.locale}
      />
    );
  }

  private getTitle() {
    if (this.props.title === undefined) {
      return undefined;
    }
    if (this.props.title === false) {
      return undefined;
    }
    if (this.props.title === true) {
      return () =>
        this.props.intl.formatMessage({
          id: "tradesByMarketAndValueDateGrid.title"
        });
    }
    if (typeof this.props.title === "string") {
      return () =>
        `${this.props.title} ${this.props.intl.formatMessage({
          id: "tradesByMarketAndValueDateGrid.title"
        })}`;
    }

    return () => this.props.title;
  }

  private filterTradesBySubTypeAndMarket(trades: FxConvertedTrade[]) {
    return trades.filter(
      x =>
        x.MarketKey === this.props.marketKey &&
        x.SubType !== TradeSubType.FxConversionOpen
    );
  }

  private groupTradesByValueDate(trades: FxConvertedTrade[]): ByValueDateTrades[] {
    const map = new Map<
      number | undefined,
      { valueDate: Date | undefined; trades: FxConvertedTrade[] }
    >();

    trades.forEach(x => {
      const valueDate = x.ValueDate
        ? new Date(
            x.ValueDate.getFullYear(),
            x.ValueDate.getMonth(),
            x.ValueDate.getDate()
          )
        : undefined;
      const key = valueDate !== undefined ? valueDate.getTime() : undefined;

      if (!map.has(key)) {
        map.set(key, {
          valueDate,
          trades: []
        });
      }

      const byValueDate = map.get(key);
      byValueDate!.trades.push(x);
    });

    return Array.from(map.entries()).map(x => ({
      summary: this.getSummary(x[1].valueDate, x[1].trades),
      trades: x[1].trades
    }));
  }

  private getNonConversionTradesOnMarketByValueDate(trades: FxConvertedTrade[]) {
    const tradesOnMarket = this.filterTradesBySubTypeAndMarket(trades);
    const tradesOnMarketByValueDate = this.groupTradesByValueDate(
      tradesOnMarket
    );

    return tradesOnMarketByValueDate;
  }

  private expandedRowRender = (byValueDateTrades: ByValueDateTrades) => {
    return (
      <TradesGrid
        columns={DefaultSpotFxColumns}
        trades={byValueDateTrades.trades}
        bordered={this.props.bordered}
        pagination={this.props.pagination}
        title={false}
      />
    );
  };

  private getSummary(
    valueDate: Date | undefined,
    trades: Trade[]
  ): ValueDateSummary {
    const consideration = trades
      .map(x => x.Consideration)
      .reduce((accumulator, value) => ({
        BaseConsideration:
          accumulator.BaseConsideration && value.BaseConsideration
            ? {
              Value:
                  accumulator.BaseConsideration.Value +
                  value.BaseConsideration.Value,
              CurrencyId: accumulator.BaseConsideration.CurrencyId
            }
            : undefined,
        DeliveryConsideration:
          accumulator.DeliveryConsideration && value.DeliveryConsideration
            ? {
              Value:
                  accumulator.DeliveryConsideration.Value +
                  value.DeliveryConsideration.Value,
              CurrencyId: accumulator.DeliveryConsideration.CurrencyId
            }
            : undefined
      }));

    return {
      valueDate,
      consideration
    };
  }
}

const TradesByMarketAndValueDateGridCmpWithIntl = injectIntl(
  TradesByMarketAndValueDateGridCmp
);

const mapStateToProps = (state: AppState): PropsFromState => {
  return {
    currencies: new Map<number, Currency>(
      state.activeTradingAccount.account!.Currencies.map(x => [
        parseInt(x.Key, 10),
        x
      ])
    )
  };
};

export const TradesByMarketAndValueDateGrid = connect(mapStateToProps)(
  TradesByMarketAndValueDateGridCmpWithIntl
);
